import { useMemo } from 'react'
import { View, Text } from '@/components'
import { createStyles } from '@codeleap/styles'
import { Chat, textVariant } from '@/config'
import {
  usePollContext,
  useStateStore,
  useTranslationContext,
} from 'stream-chat-react'
import type { PollState } from 'stream-chat'
import { PollOptionList } from './PollOptionList'
import { PollActions } from './PollActions'
import { ChatLocalMesage } from '@packages/chat/src'

const MIN_POLL_WIDTH_DESKTOP = 280
const MIN_POLL_WIDTH_MOBILE = 188

type PollHeaderData = {
  enforce_unique_vote: boolean
  is_closed: boolean
  max_votes_allowed: number | null
  name: string
}

const pollHeaderSelector = (state: PollState): PollHeaderData => ({
  enforce_unique_vote: state.enforce_unique_vote,
  is_closed: state.is_closed,
  max_votes_allowed: state.max_votes_allowed,
  name: state.name,
})

export type PollCardProps = {
  isThreadParent: boolean
  message: ChatLocalMesage
}

export const PollCard = (props: PollCardProps) => {
  const { isThreadParent, message } = props
  const { poll } = usePollContext()
  const { t } = useTranslationContext()
  const { enforce_unique_vote, is_closed, max_votes_allowed, name } = useStateStore(
    poll.state,
    pollHeaderSelector,
  )

  const chatUser = Chat.Store.useUser()
  const isMyMessage = message?.user?.id === chatUser?.id

  const subtitle = useMemo(() => {
    if (is_closed) return t ? t('Vote ended') : 'Vote ended'
    if (enforce_unique_vote) return t ? t('Select one') : 'Select one'
    if (max_votes_allowed) {
      return t
        ? t('Select up to {{count}}', { count: max_votes_allowed })
        : `Select up to ${max_votes_allowed}`
    }
    return t ? t('Select one or more') : 'Select one or more'
  }, [enforce_unique_vote, is_closed, max_votes_allowed, t])

  return (
    <View style={[styles.container, isMyMessage && styles.containerMine]}>
      <View style={styles.header}>
        <Text
          text={name}
          style={[styles.title, isMyMessage && styles.titleMine]}
        />
        <Text
          text={subtitle}
          style={[styles.subtitle, isMyMessage && styles.subtitleMine]}
        />
      </View>

      <PollOptionList message={message} isThreadParent={isThreadParent} />

      {!isThreadParent ? <PollActions isMyMessage={isMyMessage} /> : null}
    </View>
  )
}

const styles = createStyles(theme => ({
  container: {
    ...theme.presets.column,
    ...theme.spacing.gap(1.5),
    borderRadius: theme.radius.regular,
    minWidth: MIN_POLL_WIDTH_DESKTOP,
    maxWidth: '100%',
    width: '100%',
    boxSizing: 'border-box',

    '@media (max-width: 1105px) and (min-width: 893px)': {
      minWidth: 0,
      maxWidth: '100%',
      width: '100%',
      ...theme.spacing.padding(1.25),
    },

    [theme.media.down('mobile')]: {
      minWidth: MIN_POLL_WIDTH_MOBILE,
      width: '100%',
      ...theme.spacing.padding(1.5),
    },
  },
  containerMine: {
    backgroundColor: theme.baseColors.primarySolid500,
  },
  header: {
    ...theme.presets.column,
    ...theme.spacing.gap(0.25),
  },
  title: {
    ...textVariant('p3'),
    fontWeight: 400,
    color: theme.baseColors.onPrimarySolid50Heading,
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
  },
  titleMine: {
    color: theme.baseColors.onPrimarySolid500Heading,
  },
  subtitle: {
    ...textVariant('c3'),
    color: theme.baseColors.onPrimarySolid50Caption,
  },
  subtitleMine: {
    color: theme.baseColors.onPrimarySolid500Caption,
  },
  optionsWrapper: {
    ...theme.presets.column,
    ...theme.spacing.gap(1),
  },
  actions: {
    ...theme.presets.row,
    ...theme.spacing.gap(1),
  },
  actionButton: {
    touchableWrapper: {
      minWidth: theme.spacing.value(10),
    },
  },
  optionRow: {
    ...theme.presets.row,
    ...theme.presets.alignStart,
    ...theme.spacing.gap(1),
    ...theme.spacing.padding(1),
    borderRadius: theme.radius.regular,
    backgroundColor: theme.baseColors.neutralBlackTransparent50,
    cursor: 'pointer',
  },
  optionRowMine: {
    backgroundColor: theme.baseColors.primaryTransparent100,
  },
  optionRowDisabled: {
    cursor: 'default',
    opacity: 0.6,
  },
  optionLeading: {
    ...theme.presets.center,
    height: theme.spacing.value(3),
  },
  checkbox: {
    ...theme.presets.center,
    ...theme.sized(2.5),
    borderRadius: theme.radius.small,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: theme.baseColors.primarySolid500,
    backgroundColor: 'transparent',
  },
  checkboxMine: {
    borderColor: theme.baseColors.neutralSolid0,
  },
  checkboxChecked: {
    backgroundColor: theme.baseColors.primarySolid500,
  },
  checkboxCheckedMine: {
    backgroundColor: theme.baseColors.neutralSolid0,
  },
  checkboxIcon: {
    color: theme.baseColors.neutralSolid0,
    ...theme.sized(1.5),
  },
  checkboxIconMine: {
    color: theme.baseColors.primarySolid500,
  },
  optionContent: {
    ...theme.presets.column,
    ...theme.spacing.gap(0.5),
    flex: 1,
  },
  optionHeader: {
    ...theme.presets.row,
    ...theme.presets.alignCenter,
    ...theme.presets.justifySpaceBetween,
    ...theme.spacing.gap(1),
  },
  optionText: {
    ...textVariant('p3'),
    color: theme.colors.textHeadingRegular,
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
  },
  optionTextMine: {
    color: theme.baseColors.neutralSolid0,
  },
  voteCount: {
    ...textVariant('c3'),
    color: theme.colors.textCaption,
  },
  voteCountMine: {
    color: theme.baseColors.neutralSolid0,
  },
  progressBar: {
    position: 'relative',
    width: '100%',
    height: theme.spacing.value(0.75),
    borderRadius: theme.radius.small,
    overflow: 'hidden',
    backgroundColor: theme.baseColors.neutralBlackTransparent100,
  },
  progressBarMine: {
    backgroundColor: theme.baseColors.neutralWhiteTransparent250,
  },
  progressFill: {
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100%',
    backgroundColor: theme.baseColors.primarySolid500,
  },
  progressFillMine: {
    backgroundColor: theme.baseColors.neutralSolid0,
  },
  progressFillWinner: {
    backgroundColor: theme.baseColors.primarySolid700,
  },
  avatars: {
    ...theme.presets.row,
    ...theme.spacing.gap(0.5),
  },
}))
